import React, { useRef } from 'react';
import { DayProps } from '../types/Calendar.types';

export const Day: React.FC<DayProps> = ({
  day,
  date,
  isSelected,
  isInRange,
  isStart,
  isEnd,
  isFirstInRange,
  isLastInRange,
  afterSelected,
  isDisabled,
  isToday,
  isHovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
  className = '',
  renderDay,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  if (day === null) {
    return <div className="ui-h-control-xs ui-w-control-xs" />;
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!isDisabled) {
      onClick(date);
    }
  };

  const handleMouseEnter = () => {
    if (!isDisabled && onMouseEnter) {
      // Limpiar el timeout anterior si existe
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      onMouseEnter(date);
    }
  };

  const handleMouseLeave = () => {
    if (!isDisabled && onMouseLeave) {
      // Solo aplicar timeout si no estamos en un día seleccionado o en rango
      // para mantener el hover visual funcionando correctamente
      if (isSelected || isInRange || isStart || isEnd) {
        timeoutRef.current = setTimeout(() => {
          onMouseLeave();
          timeoutRef.current = null;
        }, 100);
      } else {
        onMouseLeave();
      }
    }
  };

  const getDayClassName = () => {
    let baseClasses = `ui-w-control-xs ui-h-control-xs ui-flex ui-items-center ui-select-none ui-justify-center ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs ui-rounded-control-sm ${!isLastInRange && !isInRange && !isFirstInRange ? 'hover:ui-bg-surface-brand hover:ui-text-blue-500 hover:ui-cursor-pointer' : ''}  ui-text-body-xs-medium  ui-transition-colors  `;

    if (isDisabled) {
      baseClasses += ' ui-text-disabled ui-cursor-not-allowed';
    } else if (isSelected) {
      baseClasses += ' ui-bg-fill-brand ui-text-white ';
    } else if (isStart) {
      baseClasses += ` ui-bg-fill-brand ui-text-white  ${afterSelected ? 'ui-rounded-l-lg' : 'ui-rounded-r-lg'}`;
    } else if (isEnd) {
      baseClasses += ` ui-bg-fill-brand ui-text-white  ${afterSelected ? 'ui-rounded-r-lg' : 'ui-rounded-l-lg'}`;
    } else if (isInRange) {
      baseClasses += ' ui-bg-surface-brand ui-text-brand';
    } else if (isHovered) {
      baseClasses += ' ui-bg-surface-brand hover:ui-bg-surface-brand-hover';
    } else if (isToday) {
      baseClasses += ' ui-text-brand';
    } else {
      baseClasses += 'hover:ui-bg-surface-disabled';
    }

    return `${baseClasses} ${className}`;
  };

  if (renderDay) {
    return renderDay({
      day,
      date,
      isSelected,
      isInRange,
      isStart,
      isEnd,
      isFirstInRange,
      isLastInRange,
      afterSelected,
      isDisabled,
      isToday,
    });
  }

  return (
    <div
      className={getDayClassName()}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={`${day} ${date.toLocaleDateString()}`}
      aria-selected={isSelected}
      aria-disabled={isDisabled}
    >
      {day}
    </div>
  );
};
